import type { RowCondition, RowStyleRule } from './types';
import { parseDateLocal } from '@packages/core';

type RowStyleFn = (row: Record<string, unknown>, index: number) => Record<string, string | number> | undefined;

function evaluateCondition(condition: RowCondition, row: Record<string, unknown>): boolean {
  const fieldValue = row[condition.field];

  switch (condition.operator) {
    case 'EXISTS':
      return fieldValue != null && fieldValue !== '';
    case 'NOT_EXISTS':
      return fieldValue == null || fieldValue === '';
    case 'EQUAL':
      return fieldValue === condition.value;
    case 'NOT_EQUAL':
      return fieldValue !== condition.value;
    case 'DATE_PAST': {
      if (fieldValue == null || fieldValue === '') return false;
      return new Date() >= parseDateLocal(String(fieldValue));
    }
    case 'DATE_FUTURE': {
      if (fieldValue == null || fieldValue === '') return false;
      return new Date() < parseDateLocal(String(fieldValue));
    }
    default: {
      const _exhaustive: never = condition.operator;
      return false;
    }
  }
}

function evaluateRule(rule: RowStyleRule, row: Record<string, unknown>): boolean {
  if (rule.when == null) return true;

  const conditions = Array.isArray(rule.when) ? rule.when : [rule.when];
  const matchMode = rule.match ?? 'all';

  if (matchMode === 'any') {
    return conditions.some((c) => evaluateCondition(c, row));
  }
  return conditions.every((c) => evaluateCondition(c, row));
}

/**
 * Compiles an array of declarative RowStyleRule definitions into
 * a runtime function compatible with the `rowStyle` schema property.
 * Rules are evaluated in order; the first matching rule's style is returned.
 */
export function compileRowStyleRules(rules: RowStyleRule[]): RowStyleFn {
  return (row: Record<string, unknown>) => {
    for (const rule of rules) {
      if (evaluateRule(rule, row)) {
        return rule.style;
      }
    }
    return undefined;
  };
}
